/**
 * The owner's view of the launch mailing list (`/admin/subscribers`).
 *
 * Everything with a decision in it lives here rather than in the route file,
 * the way `sign-in-form.tsx` holds the sign-in page's behaviour: which page is
 * on screen, what an expired session says, and the two-press discipline in
 * front of a removal.
 *
 * ## Why a removal takes two presses
 *
 * `Api::V1::Admin::EmailSubscribersController#destroy` calls `destroy!` on the
 * row, and that row was the address's only storage location — no soft-delete
 * column, no archive table, no tombstone. There is nothing to undo *from*, so
 * the confirmation is the only thing standing between a mis-aimed press and a
 * permanently lost subscriber. It is inline rather than a Paper `Dialog`
 * (react-native-paper#4794) and rather than `window.confirm`, which would
 * block the whole tab on a modal the suite cannot drive.
 *
 * ## Why the 401 is a link rather than a redirect
 *
 * The failure app answers XHR with a JSON 401 and stores no return path — only
 * an HTML request gets one stored, and the SPA shell for this URL is served by
 * the unauthenticated catch-all, so nothing was ever stored for this page.
 * Sending the operator to `/sign-in?redirect=…` instead is exactly the open
 * redirect `routes/sign-in.tsx` explains the absence of. So the page says what
 * happened and offers a plain anchor: a real `<a>`, focusable and announced as
 * a link for free, whose full document load also picks up the fresh CSRF token
 * a new session needs.
 *
 * ## Layout
 *
 * A list, not a table. The site has to reflow at 320 px without horizontal
 * scroll (FR-019(f)), and a two-column table of addresses and dates is the one
 * shape that cannot. Each row is a `listitem` that stacks when there is no room
 * to sit side by side.
 */

import { useState } from "react";
import { StyleSheet, View } from "react-native";
import type { CSSProperties } from "react";
import {
  ActivityIndicator,
  Button,
  Divider,
  HelperText,
  Text,
} from "react-native-paper";

import {
  HttpError,
  useDeleteEmailSubscriber,
  useEmailSubscribers,
} from "@/lib/api-store";
import type { AdminEmailSubscriber } from "@/lib/api-store";
import { CONNECTION_FAILED_MESSAGE } from "@/components/forms/form-shared";
import { formatSignupDate } from "@/lib/format-signup-date";
import { headingProps } from "@/lib/heading-props";
import { useAppTheme } from "@/theme/paper-theme";
import { radius, spacing } from "@/theme/tokens";

const HEADLINE = "Mailing list";

/** The status the failure app answers a dead or absent operator session with. */
const UNAUTHORIZED_STATUS = 401;

const SESSION_ENDED_MESSAGE =
  "Your session has ended. Sign in again to see the list.";
const SIGN_IN_LINK_TEXT = "Sign in again";

const LOADING_MESSAGE = "Loading subscribers…";
const EMPTY_MESSAGE = "No one has signed up yet.";
const SIGNUP_DATE_ZONE_NOTE = "Signup dates shown in UTC.";
const RETRY_LABEL = "Try again";

const CONFIRM_LABEL = "Confirm removal";
const CANCEL_LABEL = "Cancel";
const DELETE_FAILED_MESSAGE =
  "That address couldn't be removed. Try again in a moment.";

const PAGE_HEADING_PROPS = headingProps(1);

/** The one `<h1>` this page carries — the same rule every other route follows. */
const DELETE_ERROR_ID = "subscriber-delete-error";

/**
 * The anchor's own box, in DOM styles rather than a react-native-web
 * `StyleSheet`, for the same reason `not-found.tsx` uses them: this element
 * really is an `<a>`. The theme colour is merged in at render.
 */
const SIGN_IN_LINK_STYLE: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: radius.sm,
  padding: spacing.xs,
  textDecoration: "underline",
};

function subscriberCountLabel(totalCount: number): string {
  return totalCount === 1 ? "1 subscriber" : `${totalCount} subscribers`;
}

export function SubscriberList() {
  const theme = useAppTheme();
  const [page, setPage] = useState(1);

  /**
   * Which row is asking to be confirmed. At most one at a time, which is what
   * lets the confirmation strip and its failure message use fixed ids and
   * unqualified button labels without ambiguity.
   */
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const subscribers = useEmailSubscribers(page);
  const removal = useDeleteEmailSubscriber();

  const sessionEnded =
    subscribers.error instanceof HttpError &&
    subscribers.error.status === UNAUTHORIZED_STATUS;

  function startConfirming(id: string) {
    removal.reset();
    setConfirmingId(id);
  }

  function stopConfirming() {
    removal.reset();
    setConfirmingId(null);
  }

  /**
   * Removing the only row on a page leaves the operator looking at a page that
   * no longer exists — pagy serves it as empty rather than erroring, so nothing
   * else would tell them. Stepping back happens here, before the refetch the
   * mutation's own `onSuccess` triggers, so only one request follows the
   * delete.
   */
  function handleConfirmedRemoval(id: string, rowsOnPage: number) {
    removal.mutate(id, {
      onSuccess: () => {
        setConfirmingId(null);
        if (rowsOnPage === 1 && page > 1) {
          setPage(page - 1);
        }
      },
    });
  }

  if (sessionEnded) {
    return (
      <View style={styles.notice}>
        <Text
          variant="headlineMedium"
          style={{ color: theme.colors.onSurface }}
          {...PAGE_HEADING_PROPS}
        >
          {HEADLINE}
        </Text>
        <Text
          variant="bodyLarge"
          style={{ color: theme.colors.onSurfaceVariant }}
        >
          {SESSION_ENDED_MESSAGE}
        </Text>
        {/* The colour is set on the anchor as well as on the text it wraps: an
            underline is painted in the colour of the box that declares it. */}
        <a
          href="/sign-in"
          style={{ ...SIGN_IN_LINK_STYLE, color: theme.colors.primary }}
        >
          <Text variant="titleMedium" style={{ color: theme.colors.primary }}>
            {SIGN_IN_LINK_TEXT}
          </Text>
        </a>
      </View>
    );
  }

  if (subscribers.isError) {
    return (
      <View style={styles.notice}>
        <Text
          variant="headlineMedium"
          style={{ color: theme.colors.onSurface }}
          {...PAGE_HEADING_PROPS}
        >
          {HEADLINE}
        </Text>
        <Text variant="bodyLarge" style={{ color: theme.colors.error }}>
          {CONNECTION_FAILED_MESSAGE}
        </Text>
        <Button mode="contained" onPress={() => subscribers.refetch()}>
          {RETRY_LABEL}
        </Button>
      </View>
    );
  }

  const page_ = subscribers.data;

  return (
    <View accessibilityLiveRegion="polite" style={styles.region}>
      <Text
        variant="headlineMedium"
        style={{ color: theme.colors.onSurface }}
        {...PAGE_HEADING_PROPS}
      >
        {HEADLINE}
      </Text>

      {page_ === undefined ? (
        <View style={styles.centeredRow}>
          <ActivityIndicator accessibilityLabel={LOADING_MESSAGE} />
          <Text
            variant="bodyLarge"
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            {LOADING_MESSAGE}
          </Text>
        </View>
      ) : page_.totalCount === 0 ? (
        <Text
          variant="bodyLarge"
          style={{ color: theme.colors.onSurfaceVariant }}
        >
          {EMPTY_MESSAGE}
        </Text>
      ) : (
        <>
          <View style={styles.summary}>
            <Text
              variant="titleMedium"
              style={{ color: theme.colors.onSurfaceVariant }}
            >
              {subscriberCountLabel(page_.totalCount)}
            </Text>
            {/* Said once here rather than appended to every row: the zone is a
                property of the whole column, and repeating it twenty times
                pushes the address itself off a narrow screen. */}
            <Text
              variant="bodySmall"
              style={{ color: theme.colors.onSurfaceVariant }}
            >
              {SIGNUP_DATE_ZONE_NOTE}
            </Text>
          </View>

          <View role="list" style={styles.list}>
            {page_.subscribers.map((subscriber) => (
              <SubscriberRow
                key={subscriber.id}
                subscriber={subscriber}
                isConfirming={confirmingId === subscriber.id}
                isRemoving={removal.isPending}
                failed={removal.isError && confirmingId === subscriber.id}
                onStartConfirming={() => startConfirming(subscriber.id)}
                onCancel={stopConfirming}
                onConfirm={() =>
                  handleConfirmedRemoval(
                    subscriber.id,
                    page_.subscribers.length,
                  )
                }
              />
            ))}
          </View>

          {page_.totalPages > 1 ? (
            <View style={styles.pagination}>
              <Button
                mode="outlined"
                disabled={page_.currentPage <= 1 || subscribers.isFetching}
                onPress={() => setPage(page_.currentPage - 1)}
              >
                Previous page
              </Button>
              <Text
                variant="bodyMedium"
                style={{ color: theme.colors.onSurfaceVariant }}
              >
                {`Page ${page_.currentPage} of ${page_.totalPages}`}
              </Text>
              <Button
                mode="outlined"
                disabled={
                  page_.currentPage >= page_.totalPages ||
                  subscribers.isFetching
                }
                onPress={() => setPage(page_.currentPage + 1)}
              >
                Next page
              </Button>
            </View>
          ) : null}
        </>
      )}
    </View>
  );
}

interface SubscriberRowProps {
  subscriber: AdminEmailSubscriber;
  isConfirming: boolean;
  isRemoving: boolean;
  failed: boolean;
  onStartConfirming: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * One address, and whichever of its two states it is in: the resting row with a
 * remove button, or the confirmation strip that names the address again and
 * says the removal is permanent.
 *
 * The resting button's accessible name carries the address (`Remove
 * someone@example.com`) so a screen reader's button list stays unambiguous —
 * twenty buttons all called "Remove" would name the same action twenty times.
 * The confirm and cancel buttons do not need the same treatment: only one row
 * confirms at a time.
 */
function SubscriberRow({
  subscriber,
  isConfirming,
  isRemoving,
  failed,
  onStartConfirming,
  onCancel,
  onConfirm,
}: SubscriberRowProps) {
  const theme = useAppTheme();

  return (
    <View role="listitem">
      <View style={styles.row}>
        <View style={styles.rowText}>
          <Text
            variant="bodyLarge"
            style={{ color: theme.colors.onSurface }}
            selectable
          >
            {subscriber.email}
          </Text>
          <Text
            variant="bodySmall"
            style={{ color: theme.colors.onSurfaceVariant }}
          >
            {formatSignupDate(subscriber.createdAt)}
          </Text>
        </View>

        {isConfirming ? (
          <View style={styles.rowActions}>
            <Button mode="text" onPress={onCancel} disabled={isRemoving}>
              {CANCEL_LABEL}
            </Button>
            <Button
              mode="contained"
              onPress={onConfirm}
              loading={isRemoving}
              disabled={isRemoving}
              buttonColor={theme.colors.error}
              textColor={theme.colors.onError}
              {...(failed ? { "aria-describedby": DELETE_ERROR_ID } : {})}
            >
              {CONFIRM_LABEL}
            </Button>
          </View>
        ) : (
          <Button
            mode="outlined"
            onPress={onStartConfirming}
            // `accessibilityLabel` rather than a bare `aria-label`: Paper's
            // Button forwards the react-native prop to the underlying
            // pressable, where react-native-web maps it to `aria-label`. A raw
            // `aria-label` is dropped on the floor before it reaches the DOM.
            accessibilityLabel={`Remove ${subscriber.email}`}
            textColor={theme.colors.error}
          >
            Remove
          </Button>
        )}
      </View>

      {isConfirming ? (
        <Text
          variant="bodySmall"
          style={{ color: theme.colors.onSurfaceVariant }}
        >
          {`Remove ${subscriber.email} from the list? This can't be undone.`}
        </Text>
      ) : null}

      {failed ? (
        <HelperText type="error" visible id={DELETE_ERROR_ID}>
          {DELETE_FAILED_MESSAGE}
        </HelperText>
      ) : null}

      <Divider style={styles.divider} />
    </View>
  );
}

const styles = StyleSheet.create({
  region: {
    gap: spacing.md,
  },
  notice: {
    alignItems: "flex-start",
    gap: spacing.md,
  },
  centeredRow: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.sm,
  },
  summary: {
    gap: spacing.xxs,
  },
  list: {
    gap: spacing.xs,
  },
  row: {
    alignItems: "center",
    flexDirection: "row",
    // The rows carry addresses of wildly different lengths, and the site has to
    // reflow at 320 px: below the wrap point the buttons drop under the text
    // rather than squeezing it into a one-word column.
    flexWrap: "wrap",
    gap: spacing.sm,
    justifyContent: "space-between",
    paddingVertical: spacing.xs,
  },
  rowText: {
    flexGrow: 1,
    // A basis rather than a width: the row is allowed to grow into the space it
    // has, but must be free to drop below the buttons when there is none.
    flexBasis: 220,
    gap: spacing.xxs,
  },
  rowActions: {
    alignItems: "center",
    flexDirection: "row",
    gap: spacing.xs,
  },
  divider: {
    marginTop: spacing.xs,
  },
  pagination: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: spacing.sm,
    paddingTop: spacing.sm,
  },
});
